// One currency formatter for the whole product.
//
// Before this there were four, in four files, disagreeing with each other:
//   AmendBriefPanel.fmtMoney        -> £10,000      (no pence, ever)
//   BriefReporting.formatGBPShort   -> £10,000      (maximumFractionDigits: 0)
//   CompleteBriefPanel.formatGBP    -> £10,000.00   (ALWAYS two decimals)
//   CompleteBriefPanel.formatGBPShort -> £10,000    (max 2, so sometimes .5)
// which is why the same figure rendered as "£10,000" on one screen and
// "£10,000.00" on the next.
//
// THE DEFAULT IS NO PENCE. Budgets, awarded amounts, totals and reporting
// figures are read at a glance, and ".00" on every one of them is noise.
// Pence appear only where sub-pound precision is actually meaningful —
// per-unit line items, or a value that genuinely isn't a round pound.

const GBP = '£';

// Coerce whatever the DB/form hands us. Numeric strings are common (Postgres
// numeric comes back as a string), so treat them as numbers rather than NaN.
function toNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(String(value).replace(/[£,\s]/g, ''));
  return Number.isFinite(n) ? n : null;
}

/**
 * Format a value as GBP.
 *
 * @param {number|string|null} value
 * @param {object}  [opts]
 * @param {boolean|'auto'} [opts.pence=false]
 *        false  — never show pence (the default: £10,000)
 *        true   — always show pence (£10,000.00) — use only where the
 *                 trailing zeros carry meaning, e.g. an invoice line
 *        'auto' — show pence only when there are any (£10,000 / £10,000.50).
 *                 This is the right choice for line items and unit prices.
 * @param {string}  [opts.fallback='—'] rendered when there's no value. An
 *        em-dash, not '£0' — "we don't have a figure" and "the figure is
 *        zero" are different things and must not look identical.
 */
export function formatGBP(value, opts = {}) {
  const { pence = false, fallback = '—' } = opts;
  const n = toNumber(value);
  if (n === null) return fallback;

  let min = 0;
  let max = 0;
  if (pence === true) {
    min = 2; max = 2;
  } else if (pence === 'auto') {
    // Round to the penny first, so floating-point dust (10000.000000001)
    // doesn't sprout decimals on an otherwise round figure.
    const hasPence = Math.round(n * 100) % 100 !== 0;
    min = hasPence ? 2 : 0;
    max = hasPence ? 2 : 0;
  }

  // Sign goes OUTSIDE the symbol: "-£2,500", not "£-2,500".
  const body = Math.abs(n).toLocaleString('en-GB', {
    minimumFractionDigits: min,
    maximumFractionDigits: max,
  });
  return (n < 0 ? '-' : '') + GBP + body;
}

// A range, collapsing to a single figure when both ends match — "£10,000"
// reads better than "£10,000 – £10,000".
export function formatGBPRange(min, max, opts = {}) {
  const { fallback = '—' } = opts;
  const lo = toNumber(min);
  const hi = toNumber(max);
  if (lo === null && hi === null) return fallback;
  if (lo !== null && hi === null) return `From ${formatGBP(lo, opts)}`;
  if (lo === null && hi !== null) return `Up to ${formatGBP(hi, opts)}`;
  if (lo === hi) return formatGBP(lo, opts);
  return `${formatGBP(lo, opts)} – ${formatGBP(hi, opts)}`;
}

// Compact form for dense reporting chrome, where a full figure would wrap.
// Deliberately NOT the default: rounding to "£5.0m" hides the difference
// between £5,010,367 and £4,999,999, which matters in a spend table.
export function formatGBPCompact(value, opts = {}) {
  const { fallback = '—' } = opts;
  const n = toNumber(value);
  if (n === null) return fallback;
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}${GBP}${(abs / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}m`;
  if (abs >= 10_000)    return `${sign}${GBP}${Math.round(abs / 1_000)}k`;
  return formatGBP(n);
}

// Bare number for form inputs that render their own £ prefix — strips the
// symbol so the value doesn't end up "££10,000" when echoed back.
export function toAmountInput(value) {
  const n = toNumber(value);
  return n === null ? '' : String(n);
}

export { toNumber as parseAmount };
